import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { host } from "../../api/ApiCalls";
import { useAuthStore } from "../../store/authStore";
import Lottie from "lottie-react";
import Loading from "../../assets/Loading.json";
import StarRating from "./StarRating";
import RateUser from "./RateUser";
import DataNotFound from "./DataNotFound";
import { toast } from "react-toastify";
import { tostifyOption } from "../../utils/toastOptions";

const CoursePortal = () => {
  const { courseId, studentId } = useParams();
  const [course, setCourse] = useState(null);
  const [studentData, setStudentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuthStore(); // Assuming you have a store for auth
  const [completedMessage, setCompletedMessage] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);
  const [uploadedFileUrl, setUploadedFileUrl] = useState(null);
  const [startSessionLoading, setStartSessionLoading] = useState(false);

  const navigate = useNavigate();


  const handleFileChange = (e) => {
    const files = e.target.files[0];
    if (!files) {
      console.error("❌ No file selected!");
      return;
    }
    setFile(files);
    console.log("📸 Selected File:", files); // ✅ Debugging: Check Selected File
  };


  

  useEffect(() => {
    const fetchPortalData = async () => {
      const token = JSON.parse(localStorage.getItem("academiaHubToken"));
      try {
        const res = await axios.get(
          `${host}/api/courses/${courseId}/portal/${studentId}`,{
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
        );
        setCourse(res.data.course);

        console.log("Course Data:", res.data.course);
        console.log("Teacher Data:", res.data.course.teacher);

        setStudentData(res.data.studentInfo);
        console.log("Student Data:", res.data.studentInfo);
      } catch (error) {
        console.error("Error loading portal data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchPortalData();
  }, [courseId, studentId]);

  const slotsReady = studentData?.selectedSlots?.length > 0;
  const lecturesReady = studentData?.lectures?.length > 0;
  useEffect(() => {
    const upcomingSession = async () => {
      if (!slotsReady || !lecturesReady) {
        return;
      }

      const sessions = studentData.selectedSlots.map((slot, i) => {
        const dateTime = new Date(slot.date).getTime();
        return { ...slot, lectureIndex: i, dateTime };
      });

      const now = Date.now();
      // const upcoming = sessions.filter(s => s.dateTime > now).sort((a, b) => a.dateTime - b.dateTime)[0];
      // Get how many lectures are already completed
      const enrolledStudent = course.students.find(
        (s) => s.student.toString() === studentId
      );

      const completedLessons = enrolledStudent?.completedLessons || 0;

      console.log("Completed Lessons:", completedLessons);
      console.log("Total Sessions:", course?.totalSessions || 0);

      // Filter out already completed lectures
      const uncompletedSessions = sessions
        .filter((_, i) => i >= completedLessons) // only include sessions not yet completed
        .filter((s) => s.dateTime > now) // and also in the future
        .sort((a, b) => a.dateTime - b.dateTime);

      const upcoming = uncompletedSessions[0];

      // console.log("Upcoming Session:", upcoming);
      // console.log(
      //   "Upcoming Session Date:",
      //   new Date(upcoming?.dateTime).toLocaleString()
      // );

      // Course completed check
      if (completedLessons >= (course?.totalSessions || 0)) {
        // updating Status to completed in database
        try {
          await axios.put(
            `${host}/api/courses/${courseId}/complete/${studentId}`
          );
          console.log("Status updated to completed in database.");
        } catch (error) {
          console.error("Error updating status:", error);
        }
        console.log("✅ All sessions completed.");
        setCompletedMessage("🎉 Course Completed!");
        setShowJoin(false);
        setNextSession(null);
        return;
      }

      if (!upcoming) {
        if (completedLessons === course.totalSessions) {
          setStartedMessage("🎉 Course Completed!");
          setShowJoin(false);
        } else {
          console.log("❌ No upcoming session found.");
        }
        return;
      }

      setNextSession(upcoming);

      const interval = setInterval(() => {
        const diff = upcoming.dateTime - Date.now();

        if (diff <= 0) {
          clearInterval(interval);
          const lecture = studentData.lectures[upcoming.lectureIndex];
          if (lecture?.started) {
            setShowJoin(true);
          } else {
            setTimeLeft("Waiting for teacher...");
          }
        } else {
          const hrs = Math.floor(diff / (1000 * 60 * 60));
          const mins = Math.floor((diff / (1000 * 60)) % 60);
          const secs = Math.floor((diff / 1000) % 60);
          setTimeLeft(`${hrs}h ${mins}m ${secs}s`);
        }
      }, 1000);

      return () => clearInterval(interval);
    };
    upcomingSession();
  }, [slotsReady, lecturesReady]);

  const [nextSession, setNextSession] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);
  const [showJoin, setShowJoin] = useState(false);
  const [startedMessage, setStartedMessage] = useState(null);

  useEffect(() => {
    // If lectures are missing but totalSessions is known
    if (
      studentData &&
      studentData.selectedSlots?.length > 0 &&
      (!studentData.lectures || studentData.lectures.length === 0) &&
      course?.totalSessions
    ) {
      const dummyLectures = Array(course.totalSessions).fill({
        started: false,
      });
      setStudentData((prev) => ({
        ...prev,
        lectures: dummyLectures,
      }));
      console.log("🧪 Injected dummy lectures for testing.");
    }
  }, [studentData, course]);

  const handleStartSession = async () => {
    setStartSessionLoading(true);
    try {
      if (!nextSession || nextSession.lectureIndex === undefined) {
        toast("No session available to start.", tostifyOption);
        setStartSessionLoading(false);
        return;
      }

      const res = await axios.put(
        `${host}/api/courses/${courseId}/start-session/${studentId}`,
        { lectureIndex: nextSession.lectureIndex }
      );

      console.log("Session started:", res.data);

      // Update lectures state
      setStudentData((prev) => ({
        ...prev,
        lectures: res.data.lectures,
      }));
      setStartSessionLoading(false);
      toast("✅ Session started successfully!", tostifyOption);
    } catch (error) {
      console.error("❌ Error starting session:", error);
      toast(error.response?.data?.message || "Failed to start session.", tostifyOption);
      setStartSessionLoading(false);
    }
  };

  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(
          `${host}/api/courses/${courseId}/session-status/${studentId}`
        );
        const { completed, currentLectureStatus, lectureIndex } = res.data;

        if (completed) {
          setStartedMessage("🎉 Course Completed!");
          setShowJoin(false);
          return;
        }

        if (currentLectureStatus) {
          setShowJoin(true);
          setStartedMessage(
            `Lecture ${lectureIndex + 1} has started. Click "Join Session"`
          );
        } else {
          setShowJoin(false);
          // setTimeLeft(`Waiting for Lecture ${lectureIndex + 1} to start...`);
        }
      } catch (err) {
        console.error("Error fetching session status", err);
      }
    }, 10000); // every 10 seconds

    return () => clearInterval(interval);
  }, [courseId, studentId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center text-gray-800 m-8">
        <div className="w-80">
          <Lottie animationData={Loading} loop={true} />
        </div>
      </div>
    );
  }
  if (!course || !studentData)
    return <DataNotFound />;

  const isTeacher = user?.role === "teacher";
  const isStudent = user?.role === "student";
  if (!isTeacher && !isStudent) {
    return (
      <div className="text-center py-20">
        You are not authorized to view this page.
      </div>
    );
  }

  const handleUploadMaterial = async () => {
    try {
      const formDataFile = new FormData();
      formDataFile.append("image", file); // ✅ key should match upload.single("file")
      console.log("📂 Uploading file:", file); // ✅ Debugging: Check File Before Upload

      const uploadRes = await axios.post(
        `${host}/api/upload/upload`,
        formDataFile,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      setUploadedFileUrl(uploadRes.data.url);
      console.log("url set: ", uploadRes.data.url);
      const materialData = {
        title,
        description,
        file: uploadRes.data.url, // ✅ Assuming the response contains the file URL
      };

      console.log("image Uploaded successfully now sending it to database");

      const res = await axios.post(
        `${host}/api/courses/${courseId}/upload-material/${studentId}`,
        materialData
      );
      console.log("Material uploaded successfully:", res.data);
      setStudentData((prev) => ({
        ...prev,
        materials: [...(prev.materials || []), materialData],
      }));
      setShowModal(false);
    } catch (error) {
      console.error("Error uploading material:", error);
      alert("Failed to upload material. Please try again.");
    }
  };

  return (
    // 🔁 Inside return(), replace old content with this updated structure:

    <div className="max-w-7xl mx-auto py-10 px-4 space-y-10">
      {/* Course Header with course Image, title and description*/}
      <div className="bg-white rounded-2xl shadow-md overflow-hidden flex flex-col md:flex-row items-center md:items-start gap-6 p-6 border border-gray-100">
        <div className="w-full md:w-1/3 rounded-xl overflow-hidden">
          <img
            src={course.image}
            alt="Course"
            className="w-full h-56 md:h-40 object-cover rounded-xl shadow-sm transition-transform duration-300 hover:scale-105"
          />
        </div>

        <div className="flex-1 text-center md:text-left">
          <h1 className="text-2xl font-bold text-gray-800 mb-2">
            {course.title}
          </h1>
          <p className="text-gray-600 text-sm leading-relaxed">
            {course.description}
          </p>
        </div>
      </div>

      {/* 📘 Course + 👨‍🏫 Teacher Info */}
      <div className="bg-white p-6 rounded-lg shadow-md space-y-6">
  {/* 📘 Course + 👨‍🏫 Teacher Info */}
  <div className="bg-white p-6 rounded-lg shadow-md space-y-8">
    {/* Teacher Info Section */}
    <div onClick={()=> navigate(`/teachers/${course.teacher._id}`)} className="cursor-pointer">
      <h2 className="text-xl font-semibold mb-4 border-b pb-2 text-blue-700 flex items-center gap-2">
        👨‍🏫 <span>Teacher Info</span>
      </h2>
      <div className="flex flex-col md:flex-row items-center md:items-start gap-4 md:gap-6">
        <img
          src={course.teacher.avatar}
          alt="avatar"
          className="w-20 h-20 rounded-full object-cover border-2 border-blue-500"
        />
        <div className="text-center md:text-left">
          <p className="text-xl font-bold text-gray-800">
            {course.teacher.name}
          </p>
          <p className="text-sm text-gray-500 mb-1">{course.teacher.email}</p>
          <StarRating rating={course.teacher.rating} />
        </div>
      </div>
    </div>

    {user?.role === "student" && (
      <div>
        <h3 className="text-lg font-semibold mb-2 text-indigo-500 flex items-center gap-2">
          ⭐ Rate Your Teacher
        </h3>
        <div className="bg-gray-50 p-4 rounded-lg shadow-inner border border-gray-200">
          <RateUser
            targetId={course.teacher._id}
            currentRating={course.teacher.rating}
            onSaved={(newRating) =>
              setCourse((prev) => ({
                ...prev,
                teacher: { ...prev.teacher, rating: newRating },
              }))
            }
          />
        </div>
      </div>
    )}
  </div>

  {/* 🎓 Student Info Section */}
  <div className="bg-white p-6 rounded-lg shadow-md space-y-8 mt-6">
    <div>
      <h2 className="text-xl font-semibold mb-4 border-b pb-2 text-green-700 flex items-center gap-2">
        🎓 <span>Student Info</span>
      </h2>
      <div className="flex flex-col md:flex-row items-center md:items-start gap-4 md:gap-6">
        <img
          src={studentData.student.avatar}
          alt="avatar"
          className="w-20 h-20 rounded-full object-cover border-2 border-green-500"
        />
        <div className="text-center md:text-left">
          <p className="text-xl font-bold text-gray-800">
            {studentData.student.name}
          </p>
          <p className="text-sm text-gray-500 mb-1">
            {studentData.student.email}
          </p>
          <StarRating rating={studentData.student.rating} />
        </div>
      </div>
    </div>

    {user?.role === "teacher" && (
      <div>
        <h3 className="text-lg font-semibold mb-2 text-green-600 flex items-center gap-2">
          ⭐ Rate Your Student
        </h3>
        <div className="bg-gray-50 p-4 rounded-lg shadow-inner border border-gray-200">
          <RateUser
            targetId={studentData.student._id}
            currentRating={studentData.student.rating}
            onSaved={(newRating) =>
              setStudentData((prev) => ({
                ...prev,
                student: { ...prev.student, rating: newRating },
              }))
            }
          />
        </div>
      </div>
    )}
  </div>
</div>


      {/* Completed Message */}
      {completedMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-5 mt-6">
          <h2 className="text-xl font-semibold mb-2">🎉 Course Completed!</h2>
          <p className="text-lg text-green-600 font-bold">{completedMessage}</p>
        </div>
      )}

      {/* 📅 Upcoming Session Countdown */}
      {nextSession && isStudent && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-5">
          <h2 className="text-xl font-semibold mb-2">⏳ Next Session</h2>
          <p className="mb-1">
            <strong>Date:</strong>{" "}
            {new Date(nextSession.date).toLocaleDateString()}
          </p>
          <p className="mb-3">
            <strong>Time:</strong> {nextSession.startTime} -{" "}
            {nextSession.endTime}
          </p>

          {!showJoin && timeLeft && (
            <p className="text-lg text-blue-600 font-bold">{timeLeft}</p>
          )}

          {showJoin && startedMessage && (
            <p className="text-lg text-blue-600 font-bold mb-2">
              {startedMessage}
            </p>
          )}

          {showJoin && (
            <Link
              to={`/live-session/${courseId}${studentId}`}
              className="mt-4 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded"
            >
              🎥 Join Session
            </Link>
          )}
        </div>
      )}

      {/* Rest of the component continues... */}
      {isTeacher && nextSession && (
        <div className="mt-10 bg-yellow-50 p-6 rounded border border-yellow-200">
          <h2 className="text-2xl font-semibold mb-4">🎓 Teacher Controls</h2>

          <p className="mb-2">
            <strong>Upcoming Lecture:</strong> #{nextSession.lectureIndex + 1}
          </p>
          <p className="mb-4 text-sm text-gray-600">
            {new Date(nextSession.date).toLocaleDateString()} —{" "}
            {nextSession.startTime} to {nextSession.endTime}
          </p>
          {showJoin ? (
            <Link
              to={`/live-session/${courseId}${studentId}`}
              className="bg-green-600 hover:bg-green-700 text-white font-semibold px-5 py-2 rounded"
            >
              🎥 Join Session
            </Link>
          ) : (
            <button
              onClick={handleStartSession}
              className="bg-purple-600 hover:bg-purple-700 text-white font-semibold px-5 py-2 rounded disabled:opacity-50"
               disabled={startSessionLoading}
            >
              {startSessionLoading ? (
    <div className="flex items-center justify-center gap-2">
      <svg
        className="animate-spin h-5 w-5 text-white"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z"
        />
      </svg>
      Starting...
    </div>
  ) : (
    "🚀 Start Session"
  )}
            </button>
          )}
        </div>
      )}

      {/* 📜 Session History Section */}
      {studentData?.lectures &&
        studentData.lectures.filter((l) => l.completed).length > 0 && (
          <div className="mt-10 bg-gray-50 p-6 rounded border border-gray-200">
            <h2 className="text-2xl font-semibold mb-4">📜 Session History</h2>

            <div className="space-y-4">
              {studentData.lectures
                .map((lecture, index) => ({
                  ...lecture,
                  slot: studentData.selectedSlots[index],
                  index,
                }))
                .filter((l) => l.completed)
                .map(({ index, slot, startTime, endTime }) => (
                  <div
                    key={index}
                    className="bg-white p-4 rounded shadow-sm border border-gray-100"
                  >
                    <p className="font-semibold text-gray-800">
                      Lecture #{index + 1}
                    </p>
                    <p className="text-sm text-gray-600">
                      <strong>Date:</strong>{" "}
                      {new Date(slot.date).toLocaleDateString()} —{" "}
                      {slot.startTime} to {slot.endTime}
                    </p>
                    <p className="text-sm text-gray-600">
                      <strong>Started At:</strong>{" "}
                      {startTime ? new Date(startTime).toLocaleString() : "N/A"}
                    </p>
                    <p className="text-sm text-gray-600">
                      <strong>Completed At:</strong>{" "}
                      {endTime ? new Date(endTime).toLocaleString() : "N/A"}
                    </p>
                  </div>
                ))}
            </div>
          </div>
        )}

      {/* 📚 Course Materials Section */}
      <div className="mt-10 bg-white p-6 rounded-lg shadow-md border border-gray-200">
        <h2 className="text-2xl font-semibold mb-6 border-b pb-3 flex items-center justify-between">
          📚 Course Materials
          {isTeacher && (
            <button
              onClick={() => setShowModal(true)} // aapka upload handler
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded text-sm font-medium transition"
            >
              + Upload Material
            </button>
          )}
        </h2>

        {studentData?.materials?.length > 0 ? (
          <div className="space-y-5 max-h-96 overflow-y-auto">
            {studentData.materials.map(({ title, description, file }, idx) => (
              <div
                key={idx}
                className="border border-gray-300 rounded-lg p-4 hover:shadow-lg transition-shadow duration-200 flex flex-col md:flex-row md:justify-between md:items-center gap-4"
              >
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-800">
                    {title}
                  </h3>
                  <p className="text-gray-600 mt-1">{description}</p>
                </div>
                {file && (
                  <a
                    href={file}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block text-blue-600 hover:underline text-sm font-medium"
                    download
                  >
                    📥 Download
                  </a>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 italic">No materials added yet.</p>
        )}
      </div>
      {showModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6">
            <h2 className="text-lg font-semibold mb-4">Upload Material</h2>

            <input
              type="text"
              placeholder="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full border px-3 py-2 mb-3 rounded text-sm"
            />

            <textarea
              placeholder="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full border px-3 py-2 mb-3 rounded text-sm"
            />

            <input
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="mb-4"
            />

            <div className="flex justify-end space-x-2">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 bg-gray-300 text-sm rounded"
              >
                Cancel
              </button>
              <button
                onClick={handleUploadMaterial}
                className="px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
              >
                Upload
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CoursePortal;
